import { useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useStore } from '@/store';
import { useIsDesktop, useMediaQuery } from '@/hooks/useMediaQuery';

export interface CollapsibleSectionProps {
  /** Keyed by "screen.sectionId" (persists within session). */
  sectionKey: string;
  title: string;
  children: React.ReactNode;
  /** Which column the section is in (affects tablet defaults). */
  column?: 'left' | 'right' | 'single';
  defaultOpen?: boolean;
  actions?: React.ReactNode;
}

/**
 * Collapsible section (P1.1.5).
 * State is keyed by "screen.sectionId" in the UI slice.
 * Default-open respects viewport:
 *   - mobile (< 768px): collapses all except first
 *   - desktop (≥ 1024px): opens all
 *   - tablet (768–1023px): opens left column, collapses right
 */
export function CollapsibleSection({
  sectionKey,
  title,
  children,
  column = 'single',
  defaultOpen,
  actions,
}: CollapsibleSectionProps) {
  const collapsed = useStore((s) => s.collapsedSections[sectionKey]);
  const setSection = useStore((s) => s.setSection);
  const isDesktop = useIsDesktop();
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1023px)');

  // Compute the viewport-aware default once the key is first encountered.
  useEffect(() => {
    const known = useStore.getState().collapsedSections[sectionKey] !== undefined;
    if (known) return;
    let open: boolean;
    if (defaultOpen !== undefined) {
      open = defaultOpen;
    } else if (isDesktop) {
      open = true;
    } else if (isTablet) {
      open = column === 'left';
    } else {
      // mobile — collapse all except the first
      open = false;
    }
    setSection(sectionKey, open);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionKey, isDesktop, isTablet, column, defaultOpen]);

  const isOpen = collapsed === undefined ? (defaultOpen ?? isDesktop) : !collapsed;
  const contentId = `section-content-${sectionKey}`;

  return (
    <section className={`collapsible ${isOpen ? 'open' : 'closed'}`.trim()}>
      <header className="collapsible-header">
        <button
          type="button"
          className="collapsible-toggle"
          aria-expanded={isOpen}
          aria-controls={contentId}
          onClick={() => setSection(sectionKey, !isOpen)}
        >
          <span className="collapsible-chevron" aria-hidden="true">
            {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </span>
          <span className="collapsible-title">{title}</span>
        </button>
        {actions ? <div className="collapsible-actions">{actions}</div> : null}
      </header>
      {isOpen ? (
        <div id={contentId} className="collapsible-body">
          {children}
        </div>
      ) : null}
    </section>
  );
}
